// src/components/Admin/FirmPortalAccount.js
// MODIFIED: Added AlertSettingsPanel for firm-wide threshold configuration
import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Divider } from '@mui/material';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import TwoFactorAuthCard from '../TwoFactorAuthCard';
import IndustryTaxonomySettings from './IndustryTaxonomySettings';
import AlertSettingsPanel from '../Alerts/AlertSettingsPanel'; // NEW

const FirmPortalAccount = ({ user, orgData }) => {
    // Check if user is enterprise admin
    const isEnterpriseAdmin = orgData?.firmPortalTier === 'enterprise' && user?.firmPortalRole === 'admin';
    
    // Check if any client in the org has Asana connected
    const [anyClientHasAsana, setAnyClientHasAsana] = useState(false);
    
    useEffect(() => {
        const checkAsanaConnections = async () => {
            if (!user?.organizationId) return;
            
            try {
                // 1. Check if any user in the org has Asana connected
                const usersQuery = query(
                    collection(db, 'users'),
                    where('organizationId', '==', user.organizationId)
                );
                const usersSnap = await getDocs(usersQuery);
                
                // Check each user's Asana integration
                let hasUserAsana = false;
                for (const userDoc of usersSnap.docs) {
                    try {
                        const asanaRef = doc(db, 'users', userDoc.id, 'integrations', 'asana');
                        const asanaSnap = await getDoc(asanaRef);
                        if (asanaSnap.exists() && asanaSnap.data()?.accessToken) {
                            hasUserAsana = true;
                            break;
                        }
                    } catch (e) {
                        // Integration doc may not exist, continue
                    }
                }
                
                // 2. Also check if any client has Asana connected
                const clientsQuery = query(
                    collection(db, 'clients'),
                    where('organizationId', '==', user.organizationId)
                );
                const clientsSnap = await getDocs(clientsQuery);
                
                let hasClientAsana = false;
                for (const clientDoc of clientsSnap.docs) {
                    try {
                        const asanaRef = doc(db, 'clients', clientDoc.id, 'integrations', 'asana');
                        const asanaSnap = await getDoc(asanaRef);
                        if (asanaSnap.exists() && asanaSnap.data()?.status === 'connected') {
                            hasClientAsana = true;
                            break;
                        }
                    } catch (e) {
                        // Integration doc may not exist, continue
                    }
                }
                
                setAnyClientHasAsana(hasUserAsana || hasClientAsana);
            } catch (err) {
                console.error('Error checking Asana connections:', err);
            }
        };
        
        checkAsanaConnections();
    }, [user?.organizationId]);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Account Settings
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Manage your profile and security settings.
            </Typography>

            {/* Grid v2 Syntax applied below: using 'size' prop instead of 'item xs' */}
            <Grid container spacing={3}>
                {/* Profile Section */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                        <Typography variant="h6" gutterBottom>
                            Profile Information
                        </Typography>
                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5 }}>
                            <Typography variant="body1" color="text.secondary">
                                Name
                            </Typography>
                            <Typography variant="body1">
                                {user?.name || 'Not set'}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5 }}>
                            <Typography variant="body1" color="text.secondary">
                                Email
                            </Typography>
                            <Typography variant="body1">
                                {user?.email || 'Not set'}
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5 }}>
                            <Typography variant="body1" color="text.secondary">
                                Role
                            </Typography>
                            <Typography variant="body1">
                                Firm Administrator
                            </Typography>
                        </Box>
                    </Paper>
                </Grid>

                {/* Security Section - 2FA */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <TwoFactorAuthCard user={user} />
                </Grid>

                 {/* NEW: Alert Settings - Enterprise Admin Only */}
                {isEnterpriseAdmin && (
                    <Grid size={{ xs: 12 }}>
                        <AlertSettingsPanel 
                            organizationId={user?.organizationId}
                            isFirmLevel={true}
                            user={user}
                            showAsana={anyClientHasAsana}
                        />
                    </Grid>
                )}

                {/* Industry Taxonomy Settings - Enterprise Admin Only */}
                {isEnterpriseAdmin && (
                    <Grid size={{ xs: 12 }}>
                        <IndustryTaxonomySettings 
                            organizationId={user?.organizationId} 
                            user={user} 
                        />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default FirmPortalAccount;